import {
  Button,
  ComponentLoader,
  FormGroup,
  FormInput,
  IconNotificationSuccess,
  IconNotificationWarning,
  Input,
  Modal,
} from '@snsw/react-component-library';
import React, { forwardRef, useEffect, useState } from 'react';
import styled, { css } from 'styled-components/macro';
import styles from './InputWithValidation.module.scss';
import { useVerifiableContactDetailApi } from 'components/VerifiableContactDetailField';

const VerifyContainer = styled.div`
  width: 20%;
  flex: 1;
  padding-top: 45px;
  ${(props) =>
    props.helpMessage
      ? css`
          padding-top: 64px;
        `
      : ''};
`;

const IconNotificationContainer = styled.div`
  padding-top: 4px;
`;

const emailLabels = {
  mainInput: 'Email address*',
  mainButton: 'Verify',
  codeSendingTitle: 'A verification code is being created...',
  codeSentTitle: 'A verification code has been sent to your email address.',
  codeInput: 'Verification code',
  submitButton: 'Submit',
  resendLink: 'Resend code',
  skipVerificationLink: 'Skip verification',
  invalidCode: 'Code is invalid. Please try again',
  verifySuccess: 'Your email address has been verified',
  verifySkip: 'Your email address has not been verified',
  emailNotValid: 'The email address is not valid.',
  emailNotVerified: 'The email address is not verified.',
};

const mobileLabels = {
  mainInput: 'Mobile number*',
  mainButton: 'Verify',
  codeSendingTitle: 'A verification code is being created...',
  codeSentTitle: 'A verification code has been sent to your mobile.',
  codeInput: 'Verification code',
  submitButton: 'Submit',
  resendLink: 'Resend code',
  skipVerificationLink: 'Skip verification',
  invalidCode: 'Code is invalid. Please try again',
  verifySuccess: 'Your mobile number has been verified',
  verifySkip: 'Your mobile number has not been verified',
  emailNotValid: 'The mobile number is not valid.',
  emailNotVerified: 'The mobile number is not verified.',
};

const InputWithValidation = ({ helpMessage, ...props }, ref) => {
  const type = props.type || 'email';

  const labels = {
    ...(type === 'mobile' ? mobileLabels : emailLabels),
    ...props.labels,
  };

  const failuresBeforeSkip = props.failuresBeforeSkip || 2;

  const [isModalOpen, setModalOpen] = useState(false);
  const [failuresCount, setFailuresCount] = useState(0);

  const baseUrl = typeof props.baseUrl !== 'undefined' ? props.baseUrl : 'http://localhost:4001';
  const sendCodePath = props.sendCodePath || '/2FA/SendCode';
  const verifyCodePath = props.verifyCodePath || '/2FA/VerifyCode';
  const { authorization } = props;

  const [email, setEmail] = useState(props.value?.email || props.value?.mobile || '');
  const [emailTouched, setEmailTouched] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState(props.value?.email || props.value?.mobile || '');
  const [code, setCode] = useState(props.value?.code || '');

  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingEmailSuccess, setSendingEmailSuccess] = useState(false);
  const [sendingEmailError, setSendingEmailError] = useState(false);

  const [sendingCode, setSendingCode] = useState(false);
  const [sendingCodeSuccess, setSendingCodeSuccess] = useState(false);
  const [sendingCodeError, setSendingCodeError] = useState(false);

  const [verificationSkipped, setVerificationSkipped] = useState(false);

  const [isValid, setIsValid] = useState(!!props.isValid);
  const [isEmailValid, setIsEmailValid] = useState(true);

  const api = useVerifiableContactDetailApi(type, baseUrl, sendCodePath, verifyCodePath, authorization);

  useEffect(() => {
    setIsValid(props.isValid);
    setIsEmailValid(props.isValid);
    if (props.isValid) {
      setVerifiedEmail(email);
    }
  }, [props.isValid]);

  useEffect(() => {
    if (ref && !ref.current) {
      ref.current = {
        setEmail: (email) => {
          setEmail(email);
          setVerifiedEmail(email);
          setIsValid(true);
        },
      };
    }
  }, []);

  useEffect(() => {
    if (props.onSkipChange) {
      props.onSkipChange(verificationSkipped);
    }
  }, [verificationSkipped]);

  useEffect(() => {
    if (props.onVerificationChange && (emailTouched || sendingCodeSuccess || sendingEmailError)) {
      props.onVerificationChange(sendingCodeSuccess || email === verifiedEmail);
    }
  }, [email, verifiedEmail, sendingCode, sendingCodeSuccess, sendingCodeError]);

  const dispatchChange = (val, email, ve) => {
    setIsValid(val);
    if (props.onChange) {
      props.onChange(email, (ve || verifiedEmail) !== email);
    }
  };

  const isValidMobile = (mobile) => {
    // eslint-disable-next-line no-control-regex
    const re =
      /^\({0,1}((|0|\+61|61)([24378]))\){0,1}( |-){0,1}[0-9]{2}(\ |-){0,1}[0-9]{2}(\ |-){0,1}[0-9]{1}(\ |-){0,1}[0-9]{3}$/i;
    return !(mobile === null || !re.test(String(mobile).toLowerCase()));
  };

  const isValidEmail = (email) => {
    // eslint-disable-next-line no-control-regex
    const re =
      /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;
    return !(email === null || !re.test(String(email).toLowerCase()));
  };

  const errorType1 = !sendingEmail && sendingEmailError;
  const errorType2 = !sendingCode && sendingCodeError;

  const triggerCodeSent = () => {
    setCode('');
    setSendingEmail(true);
    setSendingEmailSuccess(false);
    setSendingEmailError(false);
    setSendingCode(false);
    setSendingCodeSuccess(false);
    setSendingCodeError(false);
    setModalOpen(true);
    dispatchChange(false, email);
    api
      .sendCode(email)
      .then(() => {
        setSendingEmail(false);
        setSendingEmailSuccess(true);
        setSendingEmailError(false);
      })
      .catch(() => {
        setSendingEmail(false);
        setSendingEmailSuccess(false);
        setSendingEmailError(true);
      });
  };

  useEffect(() => {
    if (props.onChange && (emailTouched || sendingCodeSuccess || sendingEmailError)) {
      dispatchChange(type === 'mobile' ? isValidMobile(email) : isValidEmail(email), email);
    }
  }, [email]);

  return (
    <div className={`${styles.formContainer} ${props.className ?? ''}`}>
      <div className={`${styles.inputContainer} inputContainer`}>
        <FormInput
          placeholder={labels.mainInput}
          errorMessage={!isEmailValid ? labels.emailNotValid : labels.emailNotVerified}
          //hasError={(!isEmailValid || !isValid) && props.hasError}
          hasError={(props.value?.email && email !== verifiedEmail) || props.hasError}
          helpMessage={helpMessage}
          label={labels.mainInput}
          value={email}
          onChange={(evt) => {
            setEmail(evt.target.value);
            setEmailTouched(true);
          }}
          prefix={type !== 'mobile' ? undefined : '+61'}
        />
      </div>
      <VerifyContainer helpMessage={helpMessage}>
        {((!sendingEmail || sendingEmailError) && email !== verifiedEmail && email !== '') || !isValid ? (
          <Button
            variant={'secondary'}
            onClick={() => {
              const isValid = type === 'mobile' ? isValidMobile(email) : isValidEmail(email);
              setIsEmailValid(isValid);
              if (isValid) {
                setVerificationSkipped(false);
                setSendingCodeSuccess(false);
                triggerCodeSent();
              }
            }}
          >
            {labels.mainButton}
          </Button>
        ) : (
          <IconNotificationContainer>
            <IconNotificationSuccess size={'md'} />
          </IconNotificationContainer>
        )}
      </VerifyContainer>
      {isModalOpen ? (
        errorType1 ? ( //|| errorType2?
          <Modal
            title={
              errorType1
                ? `There was a problem sending the ${type}. Please verify your input is correct.`
                : 'There was a problem verifying the code. Please verify your input is correct.'
            }
            onClose={() => setModalOpen(false)}
            buttons={[
              {
                text: 'Close',
                onClick: () => {
                  setModalOpen(false);
                },
              },
            ]}
          />
        ) : verificationSkipped || sendingCodeSuccess ? (
          <Modal
            onClose={() => setModalOpen(false)}
            buttons={[
              {
                text: 'Close',
                onClick: () => {
                  setModalOpen(false);
                },
              },
            ]}
          >
            {sendingCodeSuccess ? <IconNotificationSuccess size={'md'} /> : <IconNotificationWarning size={'md'} />}
            <h2>{sendingCodeSuccess ? labels.verifySuccess : labels.verifySkip}</h2>
          </Modal>
        ) : (
          <Modal
            title={sendingCode || sendingEmail ? labels.codeSendingTitle : labels.codeSentTitle}
            onClose={() => setModalOpen(false)}
            buttons={[
              {
                text: labels.submitButton,
                onClick: () => {
                  setSendingCode(true);
                  setSendingCodeSuccess(false);
                  setSendingCodeError(false);
                  api
                    .verifyCode(email, code)
                    .then(() => {
                      setSendingCode(false);
                      setSendingCodeSuccess(true);
                      setSendingCodeError(false);
                      setVerifiedEmail(email);
                      dispatchChange(true, email, email);
                    })
                    .catch(() => {
                      setSendingCode(false);
                      setSendingCodeSuccess(false);
                      setSendingCodeError(true);
                      setFailuresCount(failuresCount + 1);
                    });
                },
              },
              {
                text: labels.resendLink,
                onClick: triggerCodeSent,
              },
            ]}
          >
            {sendingCode || sendingEmail ? (
              <ComponentLoader label={sendingCode ? 'Sending code...' : 'Sending message...'} fullPage={true} />
            ) : !sendingEmail && sendingEmailError ? (
              <div>There was a problem sending the email. Please verify your input is correct.</div>
            ) : (
              <>
                <FormGroup
                  id="codeVerification"
                  label={labels.codeInput}
                  errorMessage={labels.invalidCode}
                  hasError={errorType2}
                >
                  <Input
                    placeholder={labels.codeInput}
                    value={code}
                    onChange={(evt) => {
                      setCode(evt.target.value);
                    }}
                  />
                </FormGroup>
                <div className={styles.skipContainer}>
                  {!props.disableSkip && (failuresCount >= failuresBeforeSkip || failuresBeforeSkip === 0) ? (
                    <a
                      href={'#'}
                      onClick={(e) => {
                        e.preventDefault();
                        setVerificationSkipped(true);
                        // triggerModalTimeoutClose();
                      }}
                    >
                      {labels.skipVerificationLink}
                    </a>
                  ) : null}
                </div>
              </>
            )}
          </Modal>
        )
      ) : null}
    </div>
  );
};

export default forwardRef(InputWithValidation);
